<template>
  <NForm ref="formRef" class="flex flex-col h-full" :model="formValues">
    <NGrid :cols="1" :yGap="4">
      <NFormItemGridItem :label="t('canvasUI.canvasBuilder.page.form.aspectRatio')">
        <NSelect
          v-model:value="formValues.aspectRatio"
          :options="aspectRatioOptions"
          placeholder="Select Aspect Ratio"
        />
      </NFormItemGridItem>
    </NGrid>

    <CButton block class="mt-auto" :disabled="!formValues.aspectRatio" type="primary" @click="onSave">
      {{ t('canvasUI.common.done') }}
    </CButton>
  </NForm>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { NForm, NFormItemGridItem, NGrid, NSelect, type FormInst } from 'naive-ui'
import { useI18n } from 'vue-i18n'

import CButton from '@lib/components/CButton/CButton.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import { aspectRatios } from '@canvas-builder/constants/canvas.const'
import type { ComponentNode, PageProps } from '@canvas-builder/types/canvas'

const props = defineProps<{
  id: string
  data: PageProps
}>()
const { t } = useI18n()
const { deepMergeNodeById, setComponentEditMode, componentNodesById } = useCanvas()
const formRef = ref<FormInst | null>(null)
const formValues = reactive({
  aspectRatio: props.data.aspect_ratio,
})
const pageNode = computed(() => componentNodesById.value[props.id] as ComponentNode<PageProps> | undefined)
const aspectRatioOptions = computed(() =>
  // Skip Contain option for Page
  aspectRatios
    .filter((ratio) => ratio !== 'contain')
    .map((ratio) => ({
      label: getRatioLabel(ratio),
      value: ratio,
    })),
)

function getRatioLabel(ratio: string) {
  switch (ratio) {
    case 'unlocked':
      return t('canvasUI.aspectRatio.title.unlocked')
    case 'contain':
      return t('canvasUI.aspectRatio.title.contain')
    default:
      return ratio
  }
}

onMounted(() => {
  if (pageNode.value) {
    formValues.aspectRatio = pageNode.value.data?.aspect_ratio
  }
})

async function onSave() {
  formRef.value?.validate((errors) => {
    if (!errors) {
      deepMergeNodeById(props.id, {
        data: {
          aspect_ratio: formValues.aspectRatio,
        } satisfies PageProps,
      })
      setComponentEditMode(false)
    }
  })
}
</script>
